import { makeStyles } from '@material-ui/core';
import React, { useCallback } from 'react';
import Button from '#web-components/components/Button';
import Typography from '#web-components/components/Typography';
import StandardLayout from '#web-components/components/Layouts/Standard';
import { ErrorInfo } from '#web-components/types/common';
import LogoLink from '#web-components/components/Navbar/components/LogoLink';
import { ArrowLeftIcon } from '#web-components/components/Icons';
import InlineButton from '#web-components/components/InlineButton';
import FlashMessage, { ViewType } from '#web-components/components/FlashMessage';
import SystemErrorDescription from '#web-components/components/Layouts/Error/components/SystemErrorDescription';
import EmailSupportError from '#web-components/components/Layouts/Error/components/EmailSupportError';
import styles from './ErrorLayout.styles';

interface ErrorLayoutProps {
  error: ErrorInfo,
  reloadButtonCaption: string,
  defaultErrorTitle: string,
  defaultBackLinkTitle: string,
  defaultMessage: string,
  defaultDescription: string,
  onBackLinkClick: (path?: string) => void,
  navLinkComponent: React.ElementType,
  appTitle: string,
  registryName?: string;
  registryFullName?: string;
  homePath: string,
  loaderIcon: React.ReactNode;
  logoIcon: React.ReactNode;
  systemError?: {
    title: string;
    fields: {
      name: string,
      value: string,
    }[],
  };
  supportEmail?: {
    title: string,
    description: string,
    email: string,
    subject: string,
    body: string
  },
  t: (key: string) => string;
  supportChannelUrl?: string;
}

const useStyles = makeStyles(styles, { name: 'ErrorLayout' });

export default function ErrorLayout({
  error: {
    message,
    componentProps = {},
  },
  reloadButtonCaption,
  defaultErrorTitle,
  defaultBackLinkTitle,
  defaultMessage,
  defaultDescription,
  onBackLinkClick,
  navLinkComponent,
  appTitle,
  registryName,
  registryFullName,
  homePath,
  systemError,
  supportEmail,
  loaderIcon,
  logoIcon,
  t,
  supportChannelUrl,
}: ErrorLayoutProps) {
  const classes = useStyles();

  const backLinkTitle = componentProps.backLinkTitle || defaultBackLinkTitle;
  const backLinkPath = componentProps.backLink || homePath;
  const { hasRefreshBtn, hideNavigation } = componentProps;

  const handleBackLinkClick = useCallback(() => {
    onBackLinkClick(backLinkPath);
  }, [backLinkPath, onBackLinkClick]);

  const reloadPage = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <StandardLayout
      t={t}
      title={componentProps.title || defaultErrorTitle}
      supportChannelUrl={supportChannelUrl}
      registryName={registryName}
      registryFullName={registryFullName}
      backLink={{
        title: backLinkTitle,
        path: backLinkPath,
        RouterNavLink: navLinkComponent,
      }}
      loaderIcon={loaderIcon}
      sidebar={{
        logo: <LogoLink
          appTitle={appTitle}
          homePath="/"
          direction="left"
          logoIcon={logoIcon}
          navLinkComponent={navLinkComponent}
        />,
      }}
    >
      <div className={classes.root}>
        <Typography variant="h4">
          {message || defaultMessage}
        </Typography>
        <Typography variant="textRegular" className={classes.description}>
          {componentProps.description || defaultDescription}
        </Typography>

        {(systemError && supportEmail) && (
          <div className={classes.systemError}>
            <FlashMessage
              viewType={ViewType.plain}
              status="service"
              title={systemError.title}
              xpathConfig={{ titleBlock: 'systemError.title' }}
              size="small"
              message={(
                <SystemErrorDescription
                  fields={systemError.fields}
                />
            )}
            />
            <FlashMessage
              className={classes.supportEmail}
              viewType={ViewType.plain}
              status="warning"
              title={supportEmail.title}
              size="small"
              message={(
                <EmailSupportError
                  description={supportEmail.description}
                  mail={{
                    email: supportEmail.email,
                    subject: supportEmail.subject,
                    body: supportEmail.body,
                  }}
                  errorData={systemError.fields}
                />
              )}
            />
          </div>
        )}

        {hasRefreshBtn ? (
          <>
            <Button onClick={reloadPage} className={classes.button} size="large">
              {reloadButtonCaption}
            </Button>
            {!hideNavigation && (
              <InlineButton
                size="large"
                leftIcon={<ArrowLeftIcon />}
                to={backLinkPath}
                component={navLinkComponent}
                classes={{ link: classes.link }}
              >
                {backLinkTitle}
              </InlineButton>
            )}
          </>
        ) : (
          <Button onClick={handleBackLinkClick} className={classes.button} size="large">
            {backLinkTitle}
          </Button>
        )}
      </div>
    </StandardLayout>
  );
}
